import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { Box, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { CustomAvatar, Iconify, useTable } from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { TasksList } from './tasks-list';
import { getMyTasks } from '../slice';
import { selectMyTasksState } from '../selector';
import { IList, IMembers, ITasks } from '../type';
import { IFilterOption, ITaskExportField } from '../constants';
import { BoardAssigneeFilterDialog } from './board-assignee-filter-dailog';
import { DateFilterDialog } from './date-filter-dialog';
import { getProjects, selectProjectState } from '@app/modules/projects';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { selectTaskBoardState } from '@app/modules/task-board/selector';
import { getTaskBoard } from '@app/modules/task-board/slice';

const TasksListPage = () => {
  const tableProps = useTable({ defaultRowsPerPage: 100 });
  const dispatch = useDispatch();
  const filterPopupOpen = useDialog();
  const navigate = useNavigate();
  const theme = useTheme();
  const { page, rowsPerPage } = tableProps;

  const methods = useForm<any>({
    defaultValues: {
      selectedProject: '',
      filterType: null,
      filteredTasks: [],
      originalTasks: [],
      exportType: 'pdf',
      activeFilters: {},
    },
  });

  const { watch } = methods;

  const selectedProject = watch('selectedProject');
  const filterType = watch('filterType');
  const filteredTasks = watch('filteredTasks');
  const originalTasks = watch('originalTasks');
  const activeFilters = watch('activeFilters');
  const { state } = useLocation();
  const projectId = state?.id;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { data } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });
  const { data: boardsData } = useRequestState({ stateSelector: selectTaskBoardState });
  const { data: projectData } = useRequestState({ stateSelector: selectProjectState });

  useEffect(() => {
    dispatch(getProjects());
    if (data) {
      dispatch(getTaskBoard({ userId: data?.id }));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (projectData?.projects?.length && !selectedProject) {
      let initialProjectName = '';
      if (projectId) {
        const matchingProject = projectData.projects.find(
          (project: any) => project.id === projectId
        );
        initialProjectName = matchingProject ? matchingProject.name : projectData.projects[0].name;
      } else {
        initialProjectName = projectData.projects[0].name;
      }
      methods.setValue('selectedProject', initialProjectName);
    }
  }, [projectData, projectId, selectedProject, methods]);

  useEffect(() => {
    if (data?.id && selectedProject) {
      dispatch(getMyTasks({ userId: data.id, project: selectedProject }));
    }
  }, [dispatch, data?.id, selectedProject]);

  const { data: taskData, loading } = useRequestState({
    stateSelector: selectMyTasksState,
    errorShown: true,
  });
  const isAnyFilterActive = !!(
    activeFilters.project ??
    activeFilters.assignee ??
    activeFilters.date
  );

  const filteredRows = isAnyFilterActive ? filteredTasks : originalTasks;

  useEffect(() => {
    if (data?.id && selectedProject) {
      dispatch(getMyTasks({ userId: data.id, project: selectedProject, page, limit: rowsPerPage }));
    }
  }, [data?.id, dispatch, page, rowsPerPage, selectedProject]);

  const handleSearchChange = debounce((search: string) => {
    if (data?.id && selectedProject) {
      dispatch(getMyTasks({ userId: data.id, project: selectedProject, search }));
    }
  }, 1000);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const stripHtml = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent ?? tempDiv.innerText ?? '';
  };

  useEffect(() => {
    if (taskData?.tasks) {
      methods.setValue('originalTasks', taskData.tasks);
      methods.setValue('filteredTasks', taskData.tasks);
    }
  }, [taskData?.tasks, methods]);

  const handleDropDownOption = (option: any) => {
    methods.setValue('selectedProject', option);
  };

  const tableHeadCsv = [
    { key: ITaskExportField.TaskName, label: Strings.eta.taskName },
    { key: ITaskExportField.Description, label: Strings.eta.description },
    { key: ITaskExportField.ProjectName, label: Strings.eta.projectName },
    { key: ITaskExportField.AssignedBy, label: Strings.eta.assignedBy },
    { key: ITaskExportField.AssignedOn, label: Strings.eta.assignedOn },
    { key: ITaskExportField.DueDate, label: Strings.eta.dueDate },
    { key: ITaskExportField.EndTime, label: Strings.eta.endTime },
    { key: ITaskExportField.Label, label: Strings.eta.label },
    { key: ITaskExportField.Status, label: Strings.eta.status },
  ];

  const flattenData = (tasks: ITasks[]): Record<string, any> => {
    return tasks && tasks?.length > 0
      ? tasks?.map((task: ITasks) => ({
          [ITaskExportField.id]: task.id ?? '',
          [ITaskExportField.TaskName]: task.name ?? '',
          [ITaskExportField.Description]: task.description ? stripHtml(task.description) : 'N/A',
          [ITaskExportField.ProjectName]: task.projectName ?? '',
          [ITaskExportField.AssignedBy]:
            task.user?.fullName ??
            `${task.user?.firstName ?? ''} ${task.user?.lastName ?? ''}`.trim(),
          [ITaskExportField.AssignedOn]: task.createdAt ?? '',
          [ITaskExportField.DueDate]: task.dueDates?.endDate
            ? dayjs(task.dueDates.endDate).format('D MMMM YYYY')
            : 'N/A',
          [ITaskExportField.EndTime]: task.dueDates?.endDate
            ? dayjs(task.dueDates.endDate).format('D MMMM YYYY')
            : 'N/A',
          [ITaskExportField.Label]:
            task?.addedLabels && task?.addedLabels?.length > 0
              ? task?.addedLabels?.map((l: any) => l.name).join(', ')
              : 'N/A',
          [ITaskExportField.Status]:
            task?.lists?.find((data: IList) => task.listId === data.listId)?.listName ?? 'N/A',
        }))
      : [];
  };

  const handleFilterSelect = (type: 'board' | 'assignee' | 'date') => {
    methods.setValue('filterType', type);
    filterPopupOpen.show();
  };

  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'} mb={-2}>
        <Header
          title={Strings.sidebar.taskList}
          exportedData={{
            rows: isAnyFilterActive ? flattenData(filteredTasks) : flattenData(originalTasks),
            tableHeadCsv,
            csvName: 'task-list-page',
          }}
          dropDownSelect={selectedProject || 'Select'}
          dropDownOptions={
            projectData ? projectData.projects.map((project: { name: any }) => project.name) : []
          }
          rightIcon="ic_boardView"
          onClickRightIcon={() => {
            const selectedBoardObj = boardsData.find(
              (board: any) => board.projectName === selectedProject
            );
            const boardId = selectedBoardObj?.id;
            navigate(PATH_DASHBOARD.taskBoard, { state: { id: boardId } });
          }}
          tooltip="Board View"
          onClickDropDownOption={handleDropDownOption}
          additionalExportButtonTitle={'Export'}
          searchBarProps={{
            placeholder: Strings.common.search,
            onChange: (event) => {
              handleSearchChange(event.target.value);
            },
          }}
          onFilterClick={handleClick}
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ my: 1, mx: 2 }}>
        {activeFilters.assignee && (
          <Stack
            direction="row"
            alignItems="center"
            border={`1px solid ${theme.palette.grey[500]}`}
            borderRadius="5px"
            mx={1}
            width="auto"
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1} px={1} py={0.5}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: theme.palette.grey[700], fontSize: '14px' }}
                >
                  {Strings.tasks.assignee} :
                </Box>
                <CustomAvatar
                  name={activeFilters.assignee?.fullName}
                  sx={{ width: 24, height: 24, fontSize: 12, ml: 1, mr: 1 }}
                  style={{
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.info.contrastText,
                  }}
                />
                {activeFilters.assignee?.fullName}
              </Typography>
            </Stack>
            <Iconify
              icon={'material-symbols:close'}
              width={18}
              sx={{ mr: 1 }}
              onClick={() => {
                const newFilters = { ...activeFilters, assignee: undefined };
                methods.setValue('activeFilters', newFilters);
                if (data?.id && selectedProject) {
                  dispatch(
                    getMyTasks({
                      userId: data.id,
                      project: selectedProject,
                    })
                  );
                }
              }}
            />
          </Stack>
        )}
        {activeFilters.date && (
          <Stack
            direction="row"
            alignItems="center"
            border={`1px solid ${theme.palette.grey[500]}`}
            borderRadius="5px"
            mx={1}
            width={'auto'}
            mb={2}
          >
            <Typography textAlign="center" variant="body2" p={0.5} px={1}>
              <Box
                component="span"
                sx={{ fontWeight: 700, color: theme.palette.grey[700], fontSize: '14px' }}
              >
                {Strings.tasks.dateRange} :
              </Box>
              {activeFilters.date}
            </Typography>
            <Iconify
              icon={'material-symbols:close'}
              width={18}
              sx={{ mr: 1 }}
              onClick={() => {
                const newFilters = { ...activeFilters, date: undefined };
                methods.setValue('activeFilters', newFilters);
                if (data?.id && selectedProject) {
                  dispatch(
                    getMyTasks({
                      userId: data.id,
                      project: selectedProject,
                    })
                  );
                }
              }}
            />
          </Stack>
        )}
      </Stack>
      <TasksList
        tableProps={tableProps}
        totalCount={taskData?.total ? taskData?.total : 0}
        rows={filteredRows}
        loading={loading}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => {
            handleFilterSelect(IFilterOption.Date);
          }}
        >
          {Strings.tasks.filterByDateRange}
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => {
            handleFilterSelect(IFilterOption.Assignee);
          }}
        >
          {Strings.tasks.filterByAssignee}
        </MenuItem>
      </Menu>
      {filterPopupOpen.visible && filterType === IFilterOption.Assignee ? (
        <BoardAssigneeFilterDialog
          open={filterPopupOpen.visible}
          onClose={filterPopupOpen.hide}
          title={Strings.tasks.selectAssignee}
          items={originalTasks
            .flatMap((task: any) => task.members || [])
            .reduce((unique: any[], member: IMembers) => {
              const exists = unique.find((m) => m.membersUserId === member.membersUserId);
              if (!exists) {
                unique.push({
                  membersUserId: member.membersUserId,
                  id: member.membersUserId.toString(),
                  label: `${member.firstName} ${member.lastName}`,
                  avatar: member.avatarUrl,
                });
              }
              return unique;
            }, [])}
          onSelect={(item: any) => {
            const newFilters = {
              ...activeFilters,
              assignee: { id: item.membersUserId, fullName: item.label },
            };
            methods.setValue('activeFilters', newFilters);
            if (data?.id && selectedProject) {
              dispatch(
                getMyTasks({
                  userId: data.id,
                  assignee: item.label,
                  project: selectedProject,
                })
              );
            }
            filterPopupOpen.hide();
            handleClose();
          }}
        />
      ) : (
        <DateFilterDialog
          open={filterPopupOpen.visible}
          onClose={filterPopupOpen.hide}
          title={Strings.tasks.selectDateRange}
          onDateChange={function (value: [dayjs.Dayjs | null, dayjs.Dayjs | null]): void {
            const [start, end] = value;
            if (start && end) {
              const formatted = `${String(start)} to ${String(end)}`;
              const newFilters = { ...activeFilters, date: formatted };
              methods.setValue('activeFilters', newFilters);
              if (data?.id && selectedProject) {
                dispatch(
                  getMyTasks({
                    userId: data.id,
                    startDate: String(start),
                    endDate: String(end),
                    project: selectedProject,
                  })
                );
              }
              filterPopupOpen.hide();
              handleClose();
            }
          }}
        />
      )}
    </>
  );
};

export { TasksListPage };
